'use client';

import { useEffect, useMemo, useState } from 'react';
import { cn } from 'common/utils';
import PortfolioPositionItem from 'components/v2/modules/portfolio/portfolio-position-item';
import SettleDialog from 'components/v2/modules/portfolio/settle-dialog';
import SettledPositionItem from 'components/v2/modules/portfolio/settled-position-item';
import PaginationSection from 'components/v2/molecules/pagination-section';
import {
  TabButton,
  TabContent,
  Tabs,
  TabsContainer,
} from 'components/v2/molecules/tabs';
import {
  PortfolioInfoType,
  PortfolioPositionDetailType,
  PortfolioTransactionType,
} from 'service/trading-demo';

import SettleSuccessPopup, {
  SettleSuccessPopupType,
} from './settle-success-popup';

interface PortfolioPositionsSectionProps {
  className?: string;
  portfolioData?: PortfolioInfoType;
  refetchPortfolioInfoData: () => void;
  isPortfolioInfoDataFetched: boolean;
}
const perPage = 10;

const PortfolioPositionsSection = ({
  className,
  portfolioData,
  refetchPortfolioInfoData,
  isPortfolioInfoDataFetched,
}: PortfolioPositionsSectionProps) => {
  const [activeTab, setActiveTab] = useState<'active' | 'settled'>('active');

  return (
    <div className={cn('', className)}>
      <Tabs
        defaultTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab as 'active' | 'settled');
        }}
      >
        <div className="sticky top-16 z-50 -mx-3 mb-4 shadow-sm md:mx-0">
          <TabsContainer>
            <TabButton tabKey={'active'}>保有ポジション</TabButton>
            <TabButton tabKey={'settled'}>決済履歴</TabButton>
          </TabsContainer>
        </div>

        <div className="overflow-x-hidden">
          <TabContent tabKey={'active'}>
            <ActivePositions
              portfolioData={portfolioData}
              refetchPortfolioInfoData={refetchPortfolioInfoData}
              isPortfolioInfoDataFetched={isPortfolioInfoDataFetched}
            />
          </TabContent>

          <TabContent tabKey={'settled'}>
            <SettledPositions
              settledPositions={portfolioData?.data?.transactions}
              isPortfolioInfoDataFetched={isPortfolioInfoDataFetched}
            />
          </TabContent>
        </div>
      </Tabs>
    </div>
  );
};

const ActivePositions = ({
  portfolioData,
  refetchPortfolioInfoData,
  isPortfolioInfoDataFetched,
}: {
  portfolioData?: PortfolioInfoType;
  refetchPortfolioInfoData: () => void;
  isPortfolioInfoDataFetched: boolean;
}) => {
  const [positions, setPositions] = useState(
    portfolioData?.data?.positions || [],
  );
  const [successPopupData, setSuccessPopupData] =
    useState<SettleSuccessPopupType | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const totalCount = portfolioData?.data?.positions?.length || 0;
  const totalPage = Math.ceil(totalCount / perPage);
  const currentPositions = useMemo(() => {
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    return positions.slice(startIndex, endIndex);
  }, [positions, currentPage]);

  const [selectedPosition, setSelectedPosition] =
    useState<PortfolioPositionDetailType | null>(null);

  // Update positions when portfolioData changes
  useEffect(() => {
    setPositions(portfolioData?.data?.positions || []);
  }, [portfolioData]);

  const handlePositionClick = (position: PortfolioPositionDetailType) => {
    setSelectedPosition(position);
  };

  const symbolsCount = useMemo(() => {
    return new Set(positions?.map((position) => position.symbol_name))?.size;
  }, [positions]);

  // Show skeleton loading when data is not fetched yet
  if (!isPortfolioInfoDataFetched) {
    return <ActivePositionsSkeleton />;
  }

  return (
    <div className="rounded-2xl bg-white py-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between px-5">
        <span className="ml-auto text-sm text-pp-text-secondary">
          {symbolsCount}マーケット
        </span>
      </div>

      {positions.length === 0 ? (
        <div className="px-5 py-10 text-center text-pp-text-secondary">
          <div className="mb-4 text-5xl">📊</div>
          <div className="mb-2 font-semibold">保有ポジションなし</div>
        </div>
      ) : (
        <div>
          {currentPositions.map((position, index) => (
            <PortfolioPositionItem
              key={position.id}
              position={position}
              onClick={handlePositionClick}
              className={index === positions.length - 1 ? 'border-b-0' : ''}
            />
          ))}
          {totalPage > 1 && (
            <PaginationSection
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalCount={totalCount}
              perPage={perPage}
              className="my-6"
            />
          )}
        </div>
      )}

      {selectedPosition && (
        <SettleDialog
          position={selectedPosition}
          onClose={() => setSelectedPosition(null)}
          onSettleSuccess={() => {
            refetchPortfolioInfoData();
            setCurrentPage(1);
          }}
          setSuccessPopupData={setSuccessPopupData}
        />
      )}
      {successPopupData && (
        <SettleSuccessPopup
          data={successPopupData}
          onClose={() => setSuccessPopupData(null)}
        />
      )}
    </div>
  );
};

const SettledPositions = ({
  settledPositions,
  isPortfolioInfoDataFetched,
}: {
  settledPositions?: PortfolioTransactionType[];
  isPortfolioInfoDataFetched: boolean;
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalCount = settledPositions?.length || 0;
  const totalPage = Math.ceil(totalCount / perPage);
  const currentPositions = useMemo(() => {
    if (!settledPositions) return [];
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    return settledPositions.slice(startIndex, endIndex);
  }, [settledPositions, currentPage]);

  // Show skeleton loading when data is not fetched yet
  if (!isPortfolioInfoDataFetched) {
    return <SettledPositionsSkeleton />;
  }

  return (
    <div className="rounded-2xl bg-white shadow-sm">
      <div className="mb-4 flex items-center justify-between px-5 pb-1 pt-5">
        <span className="ml-auto text-sm text-pp-text-secondary">
          過去14日間
        </span>
      </div>

      {totalCount === 0 ? (
        <div className="py-10 text-center text-pp-text-secondary">
          <div className="mb-4 text-5xl">📈</div>
          <div className="mb-2 font-semibold">決済済みポジションなし</div>
        </div>
      ) : (
        <div className="">
          {currentPositions.map((position) => (
            <SettledPositionItem key={position.id} position={position} />
          ))}
          {totalPage > 1 && (
            <PaginationSection
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalCount={totalCount}
              perPage={perPage}
              className="my-6"
            />
          )}
        </div>
      )}
    </div>
  );
};

// Skeleton loading components
const PortfolioPositionSkeleton = () => (
  <div className="border-b border-gray-100 px-5 py-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {/* Symbol icon */}
        <div className="size-8 animate-[shimmer_1.5s_infinite] rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        <div className="flex flex-col gap-1">
          {/* Symbol name */}
          <div className="h-4 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          {/* Position type */}
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {/* PnL */}
        <div className="h-4 w-20 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        {/* Percentage */}
        <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between">
      <div className="flex gap-4">
        {/* Quantity */}
        <div className="flex flex-col gap-1">
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
        {/* Entry price */}
        <div className="flex flex-col gap-1">
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
        {/* Current price */}
        <div className="flex flex-col gap-1">
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
      </div>
      {/* Settle button */}
      <div className="h-8 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
    </div>
  </div>
);

const SettledPositionSkeleton = () => (
  <div className="rounded-lg border border-gray-100 bg-white p-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {/* Symbol icon */}
        <div className="size-8 animate-[shimmer_1.5s_infinite] rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        <div className="flex flex-col gap-1">
          {/* Symbol name */}
          <div className="h-4 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          {/* Position type */}
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {/* PnL */}
        <div className="h-4 w-20 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        {/* Percentage */}
        <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between">
      <div className="flex gap-4">
        {/* Quantity */}
        <div className="flex flex-col gap-1">
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
        {/* Entry price */}
        <div className="flex flex-col gap-1">
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
        {/* Exit price */}
        <div className="flex flex-col gap-1">
          <div className="h-3 w-12 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
          <div className="h-3 w-16 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
      </div>
      {/* Date */}
      <div className="h-3 w-20 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
    </div>
  </div>
);

const ActivePositionsSkeleton = () => (
  <div className="rounded-2xl bg-white py-5 shadow-sm">
    <div className="mb-4 flex items-center justify-between px-5">
      <div className="h-5 w-32 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
      <div className="h-4 w-20 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
    </div>
    {Array.from({ length: 3 }).map((_, index) => (
      <PortfolioPositionSkeleton key={`active-skeleton-${index}`} />
    ))}
  </div>
);

const SettledPositionsSkeleton = () => (
  <div className="rounded-2xl bg-white shadow-sm">
    <div className="mb-4 flex items-center justify-between px-5 pb-1 pt-5">
      <div className="h-5 w-32 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
      <div className="h-4 w-20 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
    </div>
    <div className="space-y-4 px-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <SettledPositionSkeleton key={`settled-skeleton-${index}`} />
      ))}
      {/* Summary skeleton */}
      <div className="pb-4">
        <div className="flex items-center rounded-lg bg-[#F8F9FA] p-4">
          <div className="h-4 w-24 animate-[shimmer_1.5s_infinite] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200px_100%] bg-[position:-200px_0]"></div>
        </div>
      </div>
    </div>
  </div>
);

export default PortfolioPositionsSection;
